import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { buyWithGooglePlay } from '../lib/playBilling';
import { useI18n } from '../i18n';

const keys = { gd_growgold_100: 'gold100', gd_growgold_275: 'gold275', gd_growgold_550: 'gold550' };
export default function GrowGoldShop({ native, user, prices }) {
    const { locale } = useI18n();
    const da = locale === 'da';
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [balance, setBalance] = useState(null);
    const [allowance, setAllowance] = useState(null);
    const [busy, setBusy] = useState(false);
    const [balanceStale, setBalanceStale] = useState(false);
    const userId = user?.user_id;
    const isGuest = !!user?.is_guest;
    useEffect(() => {
        let active = true;
        setBalance(null);
        setBalanceStale(false);
        setAllowance(null);
        api.get('/play-billing/catalog').then(r => { if (active) setCatalog(r.data); }).catch(() => {});
        if (userId && !isGuest) api.get('/play-billing/account')
            .then(r => { if (active) { setBalance(r.data.growgold); setAllowance(r.data.premium_allowance); } }).catch(() => {});
        return () => { active = false; };
    }, [userId, isGuest]);
    // Do not offer a monetary product until its spending/lifecycle release gate
    // is explicitly enabled server-side. Existing entitlements stay untouched.
    if (!catalog?.growgold_enabled) return null;
    async function buy(id) {
        if (!user || user.is_guest) { navigate('/register?next=/premium'); return; }
        setBusy(true);
        try {
            await buyWithGooglePlay(keys[id], 'inapp');
            toast.success(da ? 'GrowGold er tilføjet.' : 'GrowGold added.');
            // Verification has already granted the purchase. A subsequent
            // display refresh failure must not encourage a duplicate purchase.
            try {
                setBalance((await api.get('/play-billing/account')).data.growgold);
                setBalanceStale(false);
            } catch { setBalance(null); setBalanceStale(true); }
        } catch (err) { toast.error(err?.message || 'Purchase failed'); }
        finally { setBusy(false); }
    }
    return <section className="rounded-3xl border border-brand-ochre/40 bg-brand-ochre/10 p-5 mb-6" data-testid="growgold-shop">
        <h2 className="font-serif text-2xl flex items-center gap-2"><Coins className="text-brand-ochre" />GrowGold</h2>
        {balance !== null && <p className="my-3">{da ? 'Din saldo' : 'Your balance'}: {balance} GrowGold</p>}
        {balanceStale && <p className="my-3 text-sm" role="status">{da ? 'Købet er bekræftet, men saldoen kunne ikke opdateres. Genåbn siden; køb ikke igen for at gendanne saldoen.' : 'Purchase confirmed, but the balance could not refresh. Reopen this page; do not buy again to recover the balance.'}</p>}
        {allowance?.active && <p className="my-3">{da ? 'Premium-pulje' : 'Premium allowance'}: {allowance.remaining}/{allowance.limit} · {da ? 'Ny periode' : 'Period ends'}: {new Date(allowance.period_end).toLocaleDateString(locale)}</p>}
        {balance < 0 && <p className="my-3 text-sm" role="status">{da ? 'Saldoen er negativ efter et tilbageført køb. Kontakt support ved spørgsmål; nye køb modregnes først.' : 'Your balance is negative following a refunded purchase. Contact support with questions; new purchases offset this balance first.'}</p>}
        <p className="text-sm text-brand-ink2">{da
            ? 'Engangskøb uden abonnement til AI-funktioner med en GrowGold-pris i GrowDaily med Eden. Separat fra optjente spilressourcer. Kan ikke overføres eller udbetales. Købt GrowGold udløber ikke.'
            : 'One-time purchase, no subscription, for AI features displaying a GrowGold price in GrowDaily with Eden. Separate from earned game resources. No transfers or cash-out. Purchased GrowGold does not expire.'}</p>
        <p className="mt-3 text-sm"><a href="/purchase-terms" className="underline text-brand-green">{da ? 'Købsbetingelser og refusion' : 'Purchase terms and refunds'}</a> · <a href="/privacy" className="underline text-brand-green">{da ? 'Privatliv' : 'Privacy'}</a></p>
        {native ? <div className="grid sm:grid-cols-3 gap-3 mt-4">{catalog.packs.map(pack => {
            const price = prices[keys[pack.id]]?.price;
            return <button key={pack.id} onClick={() => buy(pack.id)} disabled={busy || balanceStale || !price}
                className="rounded-2xl border border-brand-ochre/40 p-4 text-left disabled:opacity-50">
                <strong>{pack.growgold} GrowGold</strong><div>{price || (da ? 'Ikke tilgængelig' : 'Unavailable')}</div>
            </button>;
        })}</div> : <p className="mt-3 text-sm">{da ? 'GrowGold-køb på web er ikke åbnet endnu.' : 'GrowGold web checkout is not available yet.'}</p>}
    </section>;
}
